import { isObject, isObjectV2 } from '@mimir/primitive/type-guards/isObject';
import type { TimedMetadata } from './fieldValue';
import isArray from 'lodash/isArray';
import type { TimedMdfUsageRule, UsageRuleTimingContext, WarningLevel } from './timedMdfUsage';
import { isInstanceOfUsageRuleTimingContext } from './timedMdfUsage';

/*
  `as const` keeps the literal type non-widening, so the constant stays assignable
  to MdfFlavor even where inference would otherwise widen it to `string` (a Vue
  `data()` object literal, for instance).
*/
export const TIMED_METADATA_FLAVOR = 'timed-metadata' as const;
export const DEFAULT_TIMED_METADATA_COLOR = '#5daec1fa';

/**
 * This interface represents a timed metadata item.
 *
 * Timed metadata may also be applied to Mimir items without a duration,
 * such as images. In this case, if the image is then later used as part of the
 * sequence of a new video item, the metadata is transferred over to the new
 * video item with the start- and end times set as expected.
 */
export interface TimedMetadataItem {
  id: string;
  generated_summary?: string;

  // The timespan covered by this log event.
  // If the event applies to an item without a duration
  // both startMs and endMs such be 0.
  startMs: number;
  endMs: number;

  /**
   * This is meant for log events that should span the full duration of an item,
   * but was initialized at a time when the media duration is unknown. This is
   * meant to be inspected when the media duration is available and if true, the "endMs"
   * property should be replaced with the full duration.
   */
  initializedWithIndefiniteEndMs?: boolean;

  // The mdf payload containing the metadata
  data: TimedMetadata;

  creationInfo?: { // Should only be undefined for items created before this field was added
    date: string  // In Date.toISOString() format
    source: 'api' | 'sidecar',
    userId: string | null, // null indicates we don't have a user id, such as for items created by sidecar ingest.
    userEmail?: string, // this is used during sending timed metadata webhook
  };

  lastModified?: {
    userId: string,
    date: string,  // In Date.toISOString() format
    userEmail?: string, // this is used during sending timed metadata webhook
  };
}


/**
 * This interface represents the timed metadata items
 * associated to a given asset.
 */
export interface TimedMetadataCollection {
  items: TimedMetadataItem[];
}

export const isTimedMetadataCollection = (obj: unknown): obj is TimedMetadataCollection => {
  return isObject(obj) && 'items' in obj && Array.isArray(obj['items']);
};


export type UpdateTimedMetadataRequestDto = {
  // TimedMetadataItem.id -> updated item, or null to remove
  // the given metadata item from the collection
  items: Record<string, TimedMetadataItem | null>
};

export function isUpdateTimedMetadataRequestDto(obj: unknown): obj is UpdateTimedMetadataRequestDto {
  return isObjectV2(obj) && 'items' in obj && (isObjectV2(obj['items']) ||  Array.isArray(obj['items']));
}

const isString = (v: unknown): v is string => !!(v && typeof v === 'string');

export const isInstanceOfTimedMdfUsageRule = (obj: unknown): obj is TimedMdfUsageRule => {
  if (!isObject(obj)) {
    return false;
  }
  const { identifier, contexts } = obj as Partial<TimedMdfUsageRule>;
  return !!(
    isString(identifier?.key) && isString(identifier?.label)
    && contexts && Array.isArray(contexts) && contexts.every(ctx => isInstanceOfUsageRuleTimingContext(ctx))
  );
};

export const isInstanceOfTimedMdfUsageRuleArray = (arr: unknown): arr is TimedMdfUsageRule[] => {
  return Array.isArray(arr) && arr.every(obj => isInstanceOfTimedMdfUsageRule(obj));
};

export const createNewTimedMdfUsageRuleObject = (key: string = '', label: string = '', contexts: UsageRuleTimingContext[] = []): TimedMdfUsageRule => {
  return {
    identifier: { key, label },
    contexts,
  };
};

export const createNewUsageRuleContext = (name: string = ''): UsageRuleTimingContext => {
  return {
    name,
    validity: {
      from: { offsetFrom: 'forever' },
      to: { offsetFrom: 'forever' }
    },
    warningLevel: 'upon_expiry',
  };
};

export type TimedMetadataItemWithUsageRule = TimedMetadataItem & { usageRule?: TimedMdfUsageRule };

export interface UsageContextSearchIndex {
  name: string;
  validFrom?: string; // can be assumed 'forever' if not defined
  validUntil?: string; // can be assumed 'forever' if not defined
  warningLevel: WarningLevel | undefined;
}
export interface UsageRuleSearchDocument {
  ruleKey: string; // corresponds to identifier.key in the TimedMdfUsageRule definition
  startMs: number;
  endMs: number;
  ingestTime: string | undefined;
  contexts: UsageContextSearchIndex[];
}

export interface TimedMetadataDispatchInfo {
  created: TimedMetadataItem[];
  deleted: TimedMetadataItem[];
  updated: TimedMetadataItem[];
}

export enum TimedMetadataWebhook {
  TIMED_METADATA_CREATE = 'timed_metadata_create',
  TIMED_METADATA_CHANGE = 'timed_metadata_change',
  TIMED_METADATA_DELETE = 'timed_metadata_delete',
}

export interface TimedMetadataWebhookDto {
  event: TimedMetadataWebhook;
  itemId: string;
  timedMetadataItems: TimedMetadataItem[];
}

export const isTimedMetadataDispatchPayload = (payload: unknown): payload is TimedMetadataDispatchInfo => {
  if (!isObject(payload)) {
    return false;
  }
  const maybeTimedMetadataDispatchInfo = payload as Partial<TimedMetadataDispatchInfo>;
  return isArray(maybeTimedMetadataDispatchInfo.created) && isArray(maybeTimedMetadataDispatchInfo.deleted) && isArray(maybeTimedMetadataDispatchInfo.updated);
};


export type TimedMetadataUpdateDto = Pick<TimedMetadataItem, 'data' | 'startMs' | 'endMs' | 'lastModified' | 'generated_summary'>;

// to support old code, // should remove after migrating
export type TimedMetadataUpdateDtoWithId = TimedMetadataUpdateDto & { id: string };

export type TimedMetadataCreateDto = Omit<TimedMetadataItem,'lastModified' | 'generated_summary' | 'creationInfo'>;

export function isTimedMetadataCreateDto(obj: unknown): obj is TimedMetadataCreateDto {
  return isObject(obj) && 'data' in obj && 'startMs' in obj;
}

export function isTimedMetadataUpdateDtoWithId(obj: unknown): obj is TimedMetadataUpdateDtoWithId {
  return isObject(obj) && 'id' in obj && ('data' in obj || 'startMs' in obj || 'endMs' in obj);
}

export function isTimedMetadataUpdateDto(obj: unknown): obj is TimedMetadataUpdateDto {
  return isObject(obj) && ('data' in obj || 'startMs' in obj || 'endMs' in obj);
}
